import { useState, useEffect, useCallback } from "react";
import HwFeatureService from "./services/HwFeatureService";
import ScreenService from "./services/ScreenService";
import BackButton from "./assets/backButton.svg";
import GearButton from "./assets/gearButton.svg";
import { getModelConfig } from "./utils/models";
import SupportInfo from "./components/Activation/SupportInfo";
import SalesInfo from "./components/Activation/SalesInfo";
import SalesCard from "./components/Activation/SalesCard";
import SalesLogos from "./components/Activation/SalesLogos";
import StyledQRCode from "./components/Activation/StyledQRCode";
import { useActivationFlow } from "./hooks/useActivationFlow";
import { handleExitApp } from "./utils/exitApp";
import { CLOSE } from "./utils/keys";
import "./ActivationScreen.scss";
import ConfirmDialog from "./components/ConfirmDialog/ConfirmDialog";

const ActivationLanding = ({ setStep, config, handleKeyDown }) => {
    const { onlyAvailableHelper, coBrandedLogo, salesCTAText, salesPhone, hidePromo,
            headerTitle, headerSubtitle, landingTitle, landingSubtitle, activateButtonText } = config;
    console.log(config);
    return (
        <div className="activation">
            <div className="sales-screen-container">
                <div className="sales-header">
                    <div className="sales-header-title">{headerTitle}</div>
                    <div className={`sales-header-subtitle ${hidePromo ? "sales-header-subtitle-non-promo" : ""}`}>
                        {headerSubtitle}</div>
                </div>
                <div className="sales-container">
                    <div className="activation-cta-container">
                        <div className="activation-cta-copy">
                            {landingTitle && <div className="copy-titles">
                                <div className="copy-title">{landingTitle}</div>
                            </div>}
                            <div className="copy-subtitles">
                                <div className="qr-activation-subtitle">{landingSubtitle} </div>
                            </div>
                        </div>
                        <button tabIndex={-1} autoFocus onKeyDown={(event) => handleKeyDown(event, 2)} onClick={() => setStep(2)} className="activate-button">
                            {activateButtonText}
                        </button>
                        <div className="activation-cta-footer">
                            <SupportInfo />
                        </div>
                    </div>
                    {!hidePromo && <div className="separator"></div>}
                    {!hidePromo && <div className="sales-info-container">
                        <SalesCard salesCTAText={salesCTAText} salesPhone={salesPhone} onlyAvailableHelper={onlyAvailableHelper} />
                    </div>}
                </div>
                <div className="sales-container">
                    <SalesLogos coBrandedLogo={coBrandedLogo} />
                </div>
            </div>
        </div>
    );
};

const NewActivationScreen = ({ code, setStep, config, handleKeyDown }) => {
    const { isMultiStep, coBrandedLogo, instructionTitle, qrPairingTitle, qrPairingSubtitle,
            computerPairingTitle, computerPairingSubtitlePrefix, computerPairingSubtitleSuffix,
            activationCodeLabel } = config;
    const qrLink = `${process.env.REACT_APP_REMOTE_CONTROL}pair/${code}`;

    return (
        <div className="activation">
            {isMultiStep && (
                <button tabIndex={-1} autoFocus onClick={() => setStep(1)} onKeyDown={(event) => handleKeyDown(event, 1)} className="back-button">
                    <img src={BackButton} alt="Back button" />
                </button>
            )}
            <div className="instructions">
                <div className="instruction-title">{instructionTitle}</div>
            </div>
            <div className="possible-activations">
                <div className="qr-activation-container">
                    <div className="qr-activation-title">{qrPairingTitle}</div>
                    <div className="qr-activation-subtitle-section">
                        <div className={ScreenService.isVertical ? "qr-activation-subtitle-vertical" : "qr-activation-subtitle"}>{qrPairingSubtitle}</div>
                    </div>
                    <StyledQRCode value={qrLink} width={258} height={258} margin={15} style={{ borderRadius: "18px" }} />
                </div>
                <div className="separator"></div>
                <div className="activation-code-container">
                    <div className="code-title">{computerPairingTitle}</div>
                    <div className="code-subtitle-section">
                        <div className={ScreenService.isVertical ? "code-subtitle-vertical" : "code-subtitle"}>
                            {computerPairingSubtitlePrefix}<b>{process.env.REACT_APP_COMPUTER_PAIRING_MANAGER_LINK}</b>{computerPairingSubtitleSuffix}
                        </div>
                    </div>
                    <div className="code-content-container">
                        <div className={ScreenService.isVertical ? `code-title-contrast-vertical` : "code-title-contrast"}>{activationCodeLabel}</div>
                        <div className={`${ScreenService.isVertical ? "vertical-code" : "code"} activation-code-content`}>{code}</div>
                        <SalesInfo/>
                    </div>
                </div>
            </div>
            <div className="contact">
                <SupportInfo everpassLogo={true} coBrandedLogo={coBrandedLogo} />
            </div>
            <button id="checks-button" className="hidden">
                <img src={GearButton} alt="Gear button" />
            </button>
        </div>
    );
};

const ActivationSteps = ({ onActivationComplete }) => {
    const activationConfig = getModelConfig().activationScreen;
    const { step, setStep, code } = useActivationFlow(onActivationComplete, activationConfig);
    const [showExitConfirm, setShowExitConfirm] = useState(false);

    const handleKeyDown = (event, targetStep) => {
        if (event.key === "Enter" && !showExitConfirm) {
            setStep(targetStep);
        }
    };

    const showExitAppPopUp = HwFeatureService.isSupported('show-exit-app-pop-up');

    const handleCancelExit = useCallback(() => {
        setShowExitConfirm(false);
        // refocus activate button
        document.querySelector('.activate-button')?.focus();
    }, []);

    useEffect(() => {
        const handleCloseKeyDown = (e) => {
            if (showExitConfirm) {
                if (CLOSE.has(e.key)) { handleCancelExit(); }
                return;
            }
            if (!CLOSE.has(e.key)) return;
            if (activationConfig.isMultiStep && step === 2) setStep(1);
            else if (showExitAppPopUp) setShowExitConfirm(true);
        };
        window.addEventListener('keydown', handleCloseKeyDown, { capture: true });
        return () => window.removeEventListener('keydown', handleCloseKeyDown, { capture: true });
    }, [step, activationConfig.isMultiStep, showExitConfirm, showExitAppPopUp, setStep, handleCancelExit]);

    return (
        <>
            {step === 1 && <ActivationLanding setStep={setStep} config={activationConfig} handleKeyDown={handleKeyDown} />}
            {step === 2 && <NewActivationScreen code={code} setStep={setStep} config={activationConfig} handleKeyDown={handleKeyDown} />}
            {/* ConfirmDialog uses norigin. init() lives in EPGContent.js, but static imports
                resolve depth-first so it runs before this module renders. */}
            {showExitConfirm && (
                <ConfirmDialog
                    title="Leaving EverPass?"
                    description="You're heading to the home screen. Stay here to keep watching."
                    cancelTitle="Stay on EverPass"
                    confirmTitle="Go to home screen"
                    onConfirmPressed={handleExitApp}
                    onCancelPressed={handleCancelExit}
                />
            )}
        </>
    );
};

export default ActivationSteps;
